import { useEffect, useMemo, useRef, useState } from "react";

import type { LatestPort } from "@/features/devices/types";
import { formatTag } from "@/features/machines/addressing";
import type { Machine } from "@/features/machines/types";
import type { AccumulatorDeltaValue, ResolvedWidgetValue } from "./types";

interface AccumulatorSample {
  acc: number;
  measuredAt: string | null;
  configHashHex: string | null;
}

export type AccumulatorDeltaMap = Map<string, AccumulatorDeltaValue>;

/**
 * Deriva produção/atividade a partir do acumulado absoluto publicado pelo ESP.
 * A primeira amostra só aquece o cálculo; a partir da segunda, o delta é
 * `accAtual - accAnterior`. Troca de config ou queda do acumulado reinicia a
 * série para não mostrar produção negativa.
 */
export function useAccumulatorDeltas(
  machine: Machine,
  latestPorts: LatestPort[],
  enabled = true,
): AccumulatorDeltaMap {
  const identity = `${machine.workspaceId}:${machine.id}:${machine.slots.map(slot => `${slot.slotIndex}/${slot.deviceId}`).join("|")}`;
  const activity = useMemo(() => ({ enabled, identity, previous: new Map<string, AccumulatorSample>(),
    deltas: new Map<string, AccumulatorDeltaValue>(), initialized: false }), [enabled, identity]);
  const [view, setView] = useState<{ activity: object | null; deltas: AccumulatorDeltaMap }>(
    () => ({ activity: null, deltas: new Map() }),
  );
  const pausedSamples = useRef(new Map<string, AccumulatorSample>());
  const slotByDeviceId = useMemo(
    () => new Map(machine.slots.map((slot) => [slot.deviceId, slot.slotIndex])),
    [machine.slots],
  );

  useEffect(() => {
    if (!enabled) {
      // Ao voltar de aba oculta, o primeiro render ainda pode carregar o
      // snapshot anterior ao novo GET. Essa amostra nao aquece uma nova dupla.
      pausedSamples.current = new Map(latestPorts.flatMap(port => {
        const acc = toFiniteNumber(port.acc);
        return acc === null ? [] : [[`${port.deviceId}:${port.portIndex}`, {
          acc, measuredAt: port.measuredAt ?? null, configHashHex: port.configHashHex ?? null,
        }] as const];
      }));
      return;
    }
    const next = new Map(activity.deltas);
    let changed = !activity.initialized;

    for (const port of latestPorts) {
      const acc = toFiniteNumber(port.acc);
      if (acc === null) continue;

      const slotIndex = slotByDeviceId.get(port.deviceId);
      if (slotIndex === undefined) continue;

      const sampleKey = `${port.deviceId}:${port.portIndex}`;
      const source = buildAccumulatorSource(slotIndex, port.portIndex);
      const previous = activity.previous.get(sampleKey);
      const current: AccumulatorSample = {
        acc,
        measuredAt: port.measuredAt ?? null,
        configHashHex: port.configHashHex ?? null,
      };

      if (sameSample(pausedSamples.current.get(sampleKey), current)) {
        if (!next.has(source)) {
          next.set(source, deriveDelta(undefined, current));
          changed = true;
        }
        continue;
      }
      pausedSamples.current.delete(sampleKey);
      if (sameSample(previous, current)) continue;

      const delta = deriveDelta(previous, current);
      next.set(source, delta);
      activity.previous.set(sampleKey, current);
      changed = true;
    }

    if (changed) {
      activity.deltas = next;
      activity.initialized = true;
      setView({ activity, deltas: next });
    }
  }, [latestPorts, slotByDeviceId, enabled, activity]);

  if (enabled && view.activity === activity) return view.deltas;
  // Um mapa vazio deixaria applyAccumulatorDelta usar o acumulado BRUTO como
  // atividade. Projeta aquecimento no primeiro render, antes mesmo do effect,
  // e nao une amostras separadas por tempo em que o painel esteve pausado.
  const warming: AccumulatorDeltaMap = new Map();
  for (const port of latestPorts) {
    const acc = toFiniteNumber(port.acc), slotIndex = slotByDeviceId.get(port.deviceId);
    if (acc === null || slotIndex === undefined) continue;
    warming.set(buildAccumulatorSource(slotIndex, port.portIndex), deriveDelta(undefined, {
      acc, measuredAt: port.measuredAt ?? null, configHashHex: port.configHashHex ?? null,
    }));
  }
  return warming;
}

function sameSample(left: AccumulatorSample | undefined, right: AccumulatorSample): boolean {
  return left !== undefined && left.acc === right.acc && left.measuredAt === right.measuredAt &&
    left.configHashHex === right.configHashHex;
}

export function applyAccumulatorDelta(
  widgetType: string,
  availabilityMode: "state" | "activity" | undefined,
  value: ResolvedWidgetValue,
  deltas: AccumulatorDeltaMap,
): ResolvedWidgetValue {
  const deltaSource = getAccumulatorSource(value);
  const needsActivityDelta =
    (widgetType === "availability" || widgetType === "production-status") &&
    availabilityMode === "activity";
  if (!deltaSource || !needsActivityDelta || !value.isAvailable || value.numericValue === null) return value;

  // A primeira amostra de uma porta nova pode chegar antes de o effect do
  // tracker atualizar o mapa. Ausência de delta significa aquecimento, não
  // autorização para classificar o total absoluto como atividade.
  const delta = deltas.get(deltaSource) ?? deriveDelta(undefined, {
    acc: value.numericValue, measuredAt: value.measuredAt, configHashHex: null,
  });

  const numericValue = delta.delta;
  return {
    ...value,
    value: numericValue,
    numericValue,
    measuredAt: delta.measuredAt ?? value.measuredAt,
    isAvailable: delta.status === "ready" && numericValue !== null,
    accumulatorDelta: delta,
  };
}

function deriveDelta(
  previous: AccumulatorSample | undefined,
  current: AccumulatorSample,
): AccumulatorDeltaValue {
  if (!previous || previous.configHashHex !== current.configHashHex) {
    return {
      currentAcc: current.acc,
      previousAcc: null,
      delta: null,
      elapsedSeconds: null,
      ratePerMinute: null,
      measuredAt: current.measuredAt,
      status: "warming",
    };
  }

  const elapsedSeconds = diffSeconds(previous.measuredAt, current.measuredAt);
  if (current.acc < previous.acc || elapsedSeconds === null || elapsedSeconds <= 0) {
    return {
      currentAcc: current.acc,
      previousAcc: previous.acc,
      delta: null,
      elapsedSeconds,
      ratePerMinute: null,
      measuredAt: current.measuredAt,
      status: "reset",
    };
  }

  const delta = current.acc - previous.acc;
  return {
    currentAcc: current.acc,
    previousAcc: previous.acc,
    delta,
    elapsedSeconds,
    ratePerMinute: (delta / elapsedSeconds) * 60,
    measuredAt: current.measuredAt,
    status: "ready",
  };
}

function getAccumulatorSource(value: ResolvedWidgetValue): string | null {
  // O delta é uma projeção do totalizador selecionado, não de qualquer
  // leitura da mesma porta. Em `v`, substituir pela diferença de `acc`
  // troca a grandeza/unidade da regra e pode inverter funcionando/parado.
  if (value.address.kind !== "real" || value.address.signal !== "acc") return null;
  return buildAccumulatorSource(value.address.slotIndex, value.address.portIndex);
}

function buildAccumulatorSource(slotIndex: number, portIndex: number): string {
  return formatTag({ kind: "real", slotIndex, portIndex, signal: "acc" });
}

function diffSeconds(left: string | null, right: string | null): number | null {
  if (!left || !right) return null;
  const previous = Date.parse(left);
  const current = Date.parse(right);
  if (!Number.isFinite(previous) || !Number.isFinite(current)) return null;
  return (current - previous) / 1000;
}

function toFiniteNumber(value: unknown): number | null {
  const numeric = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  return Number.isFinite(numeric) ? numeric : null;
}
